// Cursor management and status bar updates
import type { EditorState } from "./state/EditorState";
import type { Canvas2DRenderer } from "../core/render/Canvas2DRenderer";
import type { Tool, RGB } from "../core/tools/Tool";

export interface CursorDeps {
  state: EditorState;
  renderer: Canvas2DRenderer;
  canvas: HTMLCanvasElement;
  handTool: Tool;
  zoomTool: Tool;
  eyedropperTool: Tool;
  brushTool: Tool;
  eraserTool: Tool;
  quickSelectionTool: Tool;
  healingBrushTool: Tool;
  spotHealingTool: Tool;
  moveTool: Tool;
  /**
   * Live modifier-key / drag-state signals used to refine the cursor beyond
   * a static per-tool mapping (issues #61, #201). Optional and additive:
   * when omitted every tool falls back to its previous, unmodified cursor
   * (grab / zoom-in / crosshair), so existing callers are unaffected.
   */
  interaction?: SelectionToolInteraction;
}

/**
 * Live state the caller (main.ts) should keep updated from pointer/keyboard
 * events and feed into `cursorForTool` / `updateCursor` each frame.
 */
export interface SelectionToolInteraction {
  /** Shift currently held — Photoshop's "Add to selection" modifier. */
  shiftKey?: boolean;
  /**
   * Alt/Option currently held — "Subtract from selection" for the
   * marquee/lasso/wand/object-selection family, or "Zoom Out" for the Zoom
   * tool.
   */
  altKey?: boolean;
  /** True while the Hand tool has an active pan drag in progress (issue #201). */
  isPanning?: boolean;
  /**
   * True when the pointer is currently hovering inside the active
   * selection's bounds, for the move-cursor swap over selection-family
   * tools (issue #61).
   */
  pointerInSelection?: boolean;
}

// ── Caps Lock state (issue #42) ─────────────────────────────
// Photoshop's "Precise Cursor" toggle. When on, every tool's cursor switches
// to a small precise crosshair. Updated from key events by main.ts.

let capsLockState = false;

/** Update the Caps Lock state from a KeyboardEvent. */
export function setCapsLockFromEvent(e: { getModifierState?: (k: string) => boolean }): void {
  if (typeof e.getModifierState === "function") {
    capsLockState = e.getModifierState("CapsLock");
  }
}

/** Direct setter for tests. */
export function setCapsLockForTesting(on: boolean): void {
  capsLockState = on;
}

/** Current Caps Lock state. */
export function isCapsLockOn(): boolean {
  return capsLockState;
}

export function updateStatusBar(deps: CursorDeps): void {
  const zoomEl = document.querySelector<HTMLSpanElement>(".zoom-control .value");
  if (zoomEl) zoomEl.textContent = `${Math.round(deps.state.viewport.zoom * 100)}%`;
  const docInfo = document.querySelector<HTMLSpanElement>(".statusbar .muted");
  if (docInfo) {
    docInfo.textContent = deps.state.doc
      ? `Doc: ${deps.state.doc.width} × ${deps.state.doc.height} px · RGB/8`
      : "No document";
  }
}

export function updateDropHint(deps: CursorDeps): void {
  const hint = document.querySelector<HTMLElement>(".drop-hint");
  if (hint) hint.style.display = deps.state.doc ? "none" : "";
}

/**
 * Map an active tool to its canvas cursor.
 *
 * Photoshop sets a tool-specific cursor whenever the pointer hovers over the
 * canvas. We match identity for the tools wired into CursorDeps (Move /
 * Hand / Zoom / Eyedropper / Brush / Eraser / Quick-Selection / Healing /
 * Spot-Healing) and then fall back to id-based matching for the rest so the
 * Marquee, Type, Gradient and Pencil tools also pick up the right cursor
 * without having to thread every single tool instance through CursorDeps.
 *
 * Issue #149 (Pencil: issue #71).
 */
export function updateCursor(deps: CursorDeps): void {
  const tool = deps.state.activeTool;
  if (!tool) { deps.canvas.style.cursor = "default"; return; }
  deps.canvas.style.cursor = cursorForTool(tool, deps);
}

/**
 * Pick the CSS cursor value for a given tool. Pure, exported so unit tests
 * can call it directly without rendering the canvas.
 */
export function cursorForTool(tool: Tool | null | undefined, deps: CursorDeps): string {
  if (!tool) return "default";
  const interaction = deps.interaction;

  // Identity matches first (tool instances we hold direct references to).
  // Hand: "grabbing" while an actual pan drag is in progress, else "grab"
  // (issue #201).
  if (tool === deps.handTool) return interaction?.isPanning ? "grabbing" : "grab";
  // Zoom: "zoom-out" while Alt is held (Photoshop's zoom-out modifier),
  // else the default "zoom-in" (issue #201).
  if (tool === deps.zoomTool) return interaction?.altKey ? "zoom-out" : "zoom-in";
  if (tool === deps.eyedropperTool) return "crosshair";
  if (
    tool === deps.brushTool ||
    tool === deps.eraserTool ||
    tool === deps.quickSelectionTool ||
    tool === deps.healingBrushTool ||
    tool === deps.spotHealingTool
  ) return "crosshair";
  if (tool === deps.moveTool) return "move";

  // Id-based matches for tools not threaded through CursorDeps. These cover
  // every PS tool that needs a non-default cursor:
  //   - all Marquee variants → crosshair
  //   - Type / Vertical Type / Type Mask / Vertical Type Mask → text (I-beam)
  //   - Gradient → crosshair
  //   - Lasso variants, Magic Wand, Crop, Paint Bucket, Shape, Pen → crosshair
  //   - Object Selection → crosshair (issue #292)
  //   - Pencil → crosshair, same as Brush (issue #71)
  const id = (tool as { id?: string }).id ?? "";

  // Marquee / Lasso / Magic Wand / Quick Selection / Object Selection share
  // Photoshop's Add/Subtract/Intersect combine-mode convention. When the
  // pointer is hovering inside the current selection's bounds, Photoshop
  // swaps to the move cursor to signal you can drag the selection outline
  // (issue #61); otherwise it's the usual crosshair.
  if (isSelectionFamilyToolId(id)) {
    return interaction?.pointerInSelection ? "move" : "crosshair";
  }

  if (id === "type" || id === "vertical-type" || id === "type-mask" || id === "vertical-type-mask") return "text";
  if (id === "gradient") return "crosshair";
  if (id === "crop" || id === "perspective-crop") return "crosshair";
  if (id === "paint-bucket") return "crosshair";
  if (id === "pen" || id === "freeform-pen" || id === "curvature-pen") return "crosshair";
  if (id.startsWith("shape-")) return "crosshair";
  if (id === "pencil") return "crosshair";

  return "default";
}

/**
 * Tool ids that follow Photoshop's Add/Subtract/Intersect combine-mode
 * convention (shift = add, alt = subtract, shift+alt = intersect). Shared
 * between `cursorForTool` (move-cursor swap, issue #61) and `drawOverlay`
 * (combine-mode badge, issue #61) so the two stay in sync. Also covers the
 * Object Selection tool's cursor (issue #292).
 */
function isSelectionFamilyToolId(id: string): boolean {
  return (
    id.startsWith("marquee-") ||
    id === "lasso" || id === "lasso-poly" || id === "magnetic-lasso" ||
    id === "magic-wand" || id === "quick-selection" ||
    id === "object-selection"
  );
}

export function rgbToHex(c: RGB): string {
  const h = (n: number) => n.toString(16).padStart(2, "0");
  return `${h(c.r)}${h(c.g)}${h(c.b)}`;
}

export function updateSwatches(deps: CursorDeps): void {
  const root = document.documentElement;
  const fg = deps.state.fgColor;
  const bg = deps.state.bgColor;
  root.style.setProperty("--fg-color", `rgb(${fg.r}, ${fg.g}, ${fg.b})`);
  root.style.setProperty("--bg-color", `rgb(${bg.r}, ${bg.g}, ${bg.b})`);
  const hex = document.querySelector<HTMLInputElement>(".hex-input");
  if (hex && document.activeElement !== hex) {
    hex.value = rgbToHex(fg);
  }
}

export function showError(msg: string): void {
  const bar = document.querySelector<HTMLElement>(".statusbar");
  if (!bar) { console.error(msg); return; }
  const note = document.createElement("span");
  note.className = "status-error";
  note.textContent = msg;
  note.title = "Click to dismiss";
  note.style.cursor = "pointer";
  note.addEventListener("click", () => note.remove());
  bar.appendChild(note);
}

export function showTip(msg: string): void {
  const bar = document.querySelector<HTMLElement>(".statusbar");
  if (!bar) return;
  const el = document.createElement("span");
  el.className = "status-tip";
  el.textContent = "💡 " + msg;
  bar.appendChild(el);
  setTimeout(() => el.remove(), 8000);
}

// ── Cursor overlay drawing (issue #42) ──────────────────────
//
// Photoshop draws extra visuals over the canvas as the pointer moves:
//   - Brush-size circle for paint tools (in canvas-pixel units → scales w/ zoom)
//   - Sampling ring for the Eyedropper (split: top = new, bottom = previous)
//   - Precise crosshair when Caps Lock is on (overrides everything)
//
// The drawOverlay function below renders these onto the cursor canvas. It is
// called from main.ts once per requestAnimationFrame.

/**
 * Tools that should render a brush-size circle at the pointer. The radius is
 * computed in *canvas-pixel* units (tool.settings.size / 2) then scaled by
 * viewport.zoom and DPR for the screen.
 */
const BRUSH_CURSOR_TOOLS = new Set([
  "brush",
  "eraser",
  "pencil",
  "clone-stamp",
  "pattern-stamp",
  "mixer-brush",
  "color-replacement",
  "history-brush",
  "art-history-brush",
  "healing-brush",
  "spot-healing",
  "smudge",
  "blur",
  "sharpen",
  "dodge",
  "burn",
  "sponge",
  "background-eraser",
  "retouch",
  "dodge-burn",
  "quick-selection",
]);

export interface OverlayParams {
  /** Overlay canvas (device-pixel sized). */
  canvas: HTMLCanvasElement;
  /** Active tool. Anything object-shaped is fine; we read .id / .settings. */
  tool: {
    id?: string;
    settings?: { size?: number };
    size?: number;
    showSamplingRing?: boolean;
    currentSample?: RGB | null;
    previousColor?: RGB | null;
  } | null;
  /** Pointer position in CSS pixels relative to the canvas. */
  pointerCss: { x: number; y: number };
  /** Viewport zoom (1 = 100%). */
  zoom: number;
  /** Foreground color (used as the previous-color half of the eyedropper ring). */
  fgColor: RGB;
  /** Device pixel ratio. */
  dpr: number;
  /**
   * Combine-mode modifier state for the +/−/× badge drawn near the
   * crosshair on marquee/lasso/wand/object-selection tools (issue #61).
   * Shift = add, Alt = subtract, Shift+Alt = intersect. Optional and
   * additive — omitting it (or holding neither key) draws no badge, same
   * as before this field existed.
   */
  combineModifiers?: { shiftKey?: boolean; altKey?: boolean } | null;
}

/**
 * Draw the cursor overlay onto the supplied canvas. Issue #42.
 *
 * Handles four layers (in priority order):
 *   1. Caps Lock → precise crosshair (always wins).
 *   2. Eyedropper-family tools → split sampling ring (when showSamplingRing).
 *   3. Brush-family tools → hollow circle sized to brush diameter in
 *      canvas-pixel units, so it scales correctly with viewport zoom.
 *   4. Selection-family tools → small +/−/× combine-mode badge near the
 *      crosshair when Shift/Alt is held (issue #61).
 */
export function drawOverlay(p: OverlayParams): void {
  const ctx = p.canvas.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, p.canvas.width, p.canvas.height);
  if (!p.tool) return;

  const x = p.pointerCss.x * p.dpr;
  const y = p.pointerCss.y * p.dpr;
  const id = p.tool.id ?? "";

  // 1. Caps Lock → precise crosshair, overrides everything.
  if (capsLockState) {
    drawPreciseCrosshair(ctx, x, y, p.dpr);
    return;
  }

  // 2. Eyedropper / Color Sampler — split sampling ring.
  if (id === "eyedropper" || id === "color-sampler") {
    const showRing = p.tool.showSamplingRing !== false;
    if (showRing) {
      drawSamplingRing(
        ctx,
        x,
        y,
        p.dpr,
        p.tool.currentSample ?? null,
        p.tool.previousColor ?? p.fgColor,
      );
    }
    return;
  }

  // 3. Brush family — hollow circle at brush diameter (canvas-pixel units).
  if (BRUSH_CURSOR_TOOLS.has(id)) {
    const size =
      p.tool.settings?.size ??
      p.tool.size ??
      30;
    // size is in canvas pixels; multiply by zoom * dpr for device pixels.
    const radiusPx = Math.max(0.5, (size / 2) * p.zoom * p.dpr);
    ctx.beginPath();
    ctx.arc(x, y, radiusPx, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.6)";
    ctx.lineWidth = 1 * p.dpr;
    ctx.stroke();
    // Light dashed second ring for visibility on dark backgrounds.
    ctx.beginPath();
    ctx.arc(x, y, radiusPx, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(255, 255, 255, 0.4)";
    ctx.setLineDash([2 * p.dpr, 2 * p.dpr]);
    ctx.stroke();
    ctx.setLineDash([]);
    // Center dot.
    ctx.beginPath();
    ctx.arc(x, y, Math.max(0.5, 1 * p.dpr), 0, Math.PI * 2);
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.fill();
    return;
  }

  // 4. Selection family — combine-mode +/−/× badge when Shift/Alt is held.
  if (isSelectionFamilyToolId(id) && p.combineModifiers) {
    const { shiftKey, altKey } = p.combineModifiers;
    const mode = shiftKey && altKey ? "intersect" : altKey ? "subtract" : shiftKey ? "add" : null;
    if (mode) drawCombineBadge(ctx, x, y, p.dpr, mode);
  }
}

/**
 * Draw the small +/−/× combine-mode badge to the lower-right of the pointer
 * for marquee/lasso/wand/object-selection tools when Shift ("add"), Alt
 * ("subtract") or Shift+Alt ("intersect") is held (issue #61). Mirrors
 * Photoshop's modifier badge that previews how the next click/drag will
 * combine with the existing selection.
 */
function drawCombineBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dpr: number,
  mode: "add" | "subtract" | "intersect",
): void {
  const bx = x + 12 * dpr;
  const by = y + 12 * dpr;
  const r = 7 * dpr;

  // White-filled badge circle with a dark outline so it reads on any
  // background pixel color.
  ctx.beginPath();
  ctx.arc(bx, by, r, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
  ctx.fill();
  ctx.lineWidth = 1 * dpr;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.85)";
  ctx.stroke();

  // Glyph: + (add), − (subtract), × (intersect).
  const arm = 3.5 * dpr;
  ctx.lineWidth = 1.2 * dpr;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.9)";
  ctx.beginPath();
  if (mode === "add") {
    ctx.moveTo(bx - arm, by); ctx.lineTo(bx + arm, by);
    ctx.moveTo(bx, by - arm); ctx.lineTo(bx, by + arm);
  } else if (mode === "subtract") {
    ctx.moveTo(bx - arm, by); ctx.lineTo(bx + arm, by);
  } else {
    ctx.moveTo(bx - arm, by - arm); ctx.lineTo(bx + arm, by + arm);
    ctx.moveTo(bx - arm, by + arm); ctx.lineTo(bx + arm, by - arm);
  }
  ctx.stroke();
}

function drawPreciseCrosshair(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dpr: number,
): void {
  const arm = 10 * dpr;
  const gap = 2 * dpr;
  ctx.lineWidth = 1 * dpr;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.85)";
  ctx.beginPath();
  ctx.moveTo(x - arm, y); ctx.lineTo(x - gap, y);
  ctx.moveTo(x + gap, y); ctx.lineTo(x + arm, y);
  ctx.moveTo(x, y - arm); ctx.lineTo(x, y - gap);
  ctx.moveTo(x, y + gap); ctx.lineTo(x, y + arm);
  ctx.stroke();
  // White inner stroke so the crosshair stays visible on dark pixels.
  ctx.strokeStyle = "rgba(255, 255, 255, 0.7)";
  ctx.beginPath();
  ctx.moveTo(x - arm + 1, y); ctx.lineTo(x - gap - 1, y);
  ctx.moveTo(x + gap + 1, y); ctx.lineTo(x + arm - 1, y);
  ctx.moveTo(x, y - arm + 1); ctx.lineTo(x, y - gap - 1);
  ctx.moveTo(x, y + gap + 1); ctx.lineTo(x, y + arm - 1);
  ctx.stroke();
}

function drawSamplingRing(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  dpr: number,
  currentSample: RGB | null,
  previousColor: RGB,
): void {
  const radius = 28 * dpr;
  const innerRadius = 18 * dpr;

  // Top half = color being sampled (falls back to previous if not yet sampling).
  const sampleColor = currentSample ?? previousColor;
  ctx.beginPath();
  ctx.arc(x, y, radius, Math.PI, 0, false);
  ctx.arc(x, y, innerRadius, 0, Math.PI, true);
  ctx.closePath();
  ctx.fillStyle = `rgb(${sampleColor.r}, ${sampleColor.g}, ${sampleColor.b})`;
  ctx.fill();

  // Bottom half = previous color.
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI, false);
  ctx.arc(x, y, innerRadius, Math.PI, 0, true);
  ctx.closePath();
  ctx.fillStyle = `rgb(${previousColor.r}, ${previousColor.g}, ${previousColor.b})`;
  ctx.fill();

  // Outer and inner ring strokes.
  ctx.lineWidth = 1.5 * dpr;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.7)";
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(x, y, innerRadius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.lineWidth = 1 * dpr;
  ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
  ctx.beginPath();
  ctx.moveTo(x - radius, y);
  ctx.lineTo(x - innerRadius, y);
  ctx.moveTo(x + innerRadius, y);
  ctx.lineTo(x + radius, y);
  ctx.stroke();

  // Center crosshair.
  const arm = 5 * dpr;
  ctx.lineWidth = 1 * dpr;
  ctx.strokeStyle = "rgba(0, 0, 0, 0.85)";
  ctx.beginPath();
  ctx.moveTo(x - arm, y); ctx.lineTo(x + arm, y);
  ctx.moveTo(x, y - arm); ctx.lineTo(x, y + arm);
  ctx.stroke();
}
